/**
 * @file ConversationHeader.tsx
 * @description Шапка переписки (chat-window-design.md §1б–д, §4): имя
 * клиента, «Заказ Z-… › · заказ ведёт …», «Ведёт: …» (янтарным, если
 * никто), кнопка «Взять себе» / «Вернуть из архива»; второй строкой — переключатель «Служебные записи · N» и [ⓘ Сведения] (окно
 * на весь экран, кроме телефона; в компактном окне и на телефоне переключатель
 * — пункт «⋯»), меню «⋯». На телефоне слева «‹» — к списку.
 */

import { ChevronLeft, ChevronRight, Info, ListCollapse } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import type { ConversationDetail } from "../../lib/api-chat";
import { chatHeaderAction } from "./chat-actions";
import { compactOrderNumber, customerName, nameWithInitial } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";

interface Props {
  detail: ConversationDetail | null;
  /** Имя из строки списка, пока карточка грузится. */
  fallbackName: string;
  meUserId: string;
  phone: boolean;
  /** Строка с «Служебные записи · N» и «Сведения» (окно на весь экран, не телефон). */
  showToolbar: boolean;
  showInfoButton: boolean;
  serviceShown: boolean;
  serviceCount: number;
  busy: boolean;
  menu: ReactNode;
  onBack: () => void;
  onTake: () => void;
  onUnarchive: () => void;
  onToggleService: () => void;
  onOpenInfo: () => void;
  onOpenOrder: (() => void) | null;
}

export function ConversationHeader(props: Props) {
  const { detail, meUserId, phone, busy } = props;
  const action = detail ? chatHeaderAction(detail) : null;
  const order = detail?.contextOrder ?? null;
  const assignee = detail?.assignedUser ?? null;
  const leads = assignee ? (assignee.id === meUserId ? "вы" : assignee.name) : CHAT_TEXT.nobody;

  return (
    <div className="flex shrink-0 flex-col border-b border-outline-variant px-2 py-2 md:px-3">
      <div className="flex items-start gap-1">
        {phone ? (
          <Button variant="ghost" size="icon" className="size-12 shrink-0" aria-label={CHAT_TEXT.back} onClick={props.onBack}>
            <ChevronLeft className="size-5" aria-hidden />
          </Button>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1">
          <p className="truncate text-base font-semibold text-on-surface">{detail ? customerName(detail.customer) : props.fallbackName}</p>
          <p className="flex min-w-0 items-center gap-1 text-xs text-on-surface-variant">
            {order && props.onOpenOrder ? (
              <button type="button" onClick={props.onOpenOrder} className="inline-flex min-h-6 shrink-0 items-center rounded text-link outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring">
                {CHAT_TEXT.order(phone ? compactOrderNumber(order.number) : order.number)}
                <ChevronRight className="size-3.5" aria-hidden />
              </button>
            ) : (
              <span className="shrink-0">{order ? CHAT_TEXT.order(order.number) : detail ? CHAT_TEXT.generalChat : ""}</span>
            )}
            {order?.owner ? <span className="truncate">· {CHAT_TEXT.orderLeads(nameWithInitial(order.owner.name))}</span> : null}
          </p>
          {detail ? (
            <p className={cn("truncate text-xs", assignee ? "text-on-surface-variant" : "text-warning")}>
              {CHAT_TEXT.leadsNow}: {leads}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {action && !phone ? (
            <Button variant="soft" size="sm" className="min-h-12 lg:min-h-8" loading={busy} onClick={action === "take" ? props.onTake : props.onUnarchive}>
              {action === "take" ? CHAT_TEXT.take : CHAT_TEXT.unarchive}
            </Button>
          ) : null}
          {props.menu}
        </div>
      </div>
      {props.showToolbar && (props.serviceCount > 0 || props.showInfoButton) ? (
        <div className="flex flex-wrap items-center gap-1 pt-1">
          {props.serviceCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={props.serviceShown}
              className={cn("min-h-12 lg:min-h-8", props.serviceShown && "bg-surface-low")}
              onClick={props.onToggleService}
            >
              <ListCollapse className="size-4" aria-hidden />
              {CHAT_TEXT.serviceRecords(props.serviceCount)}
            </Button>
          ) : null}
          {props.showInfoButton ? (
            <Button variant="ghost" size="sm" className="min-h-12 lg:min-h-8" onClick={props.onOpenInfo}>
              <Info className="size-4" aria-hidden />
              {CHAT_TEXT.info}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
